import { useRef, useCallback, useEffect, useState } from 'react';
var SCROLL_TOP_THRESHOLD = 80;
export var useConversationScroll = function useConversationScroll(_ref) {
  var contentLength = _ref.contentLength,
      isStreaming = _ref.isStreaming,
      _ref$smooth = _ref.smooth,
      smooth = _ref$smooth === void 0 ? true : _ref$smooth,
      onScrollToTop = _ref.onScrollToTop,
      _ref$streamingContent = _ref.streamingContentSignal,
      streamingContentSignal = _ref$streamingContent === void 0 ? 0 : _ref$streamingContent;
  var scrollRef = useRef(null);

  var _useState = useState(false),
      showScrollToBottom = _useState[0],
      setShowScrollToBottom = _useState[1];

  var prevContentLengthRef = useRef(0);
  var prevScrollHeightRef = useRef(0);
  var userScrolledRef = useRef(false);
  var atTopTriggeredRef = useRef(false);
  var loadMoreRequestedRef = useRef(false); // Callback ref: when the scroll container mounts (e.g. after loading),
  // immediately scroll to bottom so the user sees the latest messages.

  var scrollCallbackRef = useCallback(function (node) {
    scrollRef.current = node;

    if (node && contentLength > 0) {
      // Use double-rAF + setTimeout to cover layout timing edge cases
      var doScroll = function doScroll() {
        if (node) node.scrollTop = node.scrollHeight;
      };

      requestAnimationFrame(function () {
        return requestAnimationFrame(doScroll);
      });
      setTimeout(doScroll, 150);
    }
  }, [contentLength]);
  var scrollToBottom = useCallback(function () {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
      setShowScrollToBottom(false);
      userScrolledRef.current = false;
    }
  }, [smooth]);
  var handleScroll = useCallback(function () {
    var el = scrollRef.current;
    if (!el) return;
    var scrollTop = el.scrollTop,
        scrollHeight = el.scrollHeight,
        clientHeight = el.clientHeight;
    var isNearBottom = scrollHeight - scrollTop - clientHeight < 80;

    if (!isNearBottom) {
      userScrolledRef.current = true;
      setShowScrollToBottom(true);
    } else {
      userScrolledRef.current = false;
      setShowScrollToBottom(false);
    }

    if (onScrollToTop && scrollTop < SCROLL_TOP_THRESHOLD) {
      if (!atTopTriggeredRef.current) {
        atTopTriggeredRef.current = true;
        loadMoreRequestedRef.current = true;
        onScrollToTop();
      }
    } else {
      atTopTriggeredRef.current = false;
    }
  }, [onScrollToTop]);
  useEffect(function () {
    var el = scrollRef.current;

    if (!el) {
      // Reset prevContentLengthRef when el is not available so that
      // when the scroll container mounts, the next effect run detects
      // the content as "added" and scrolls to bottom.
      prevContentLengthRef.current = 0;
      return;
    }

    var addedContent = contentLength > prevContentLengthRef.current;
    var prevLength = prevContentLengthRef.current;
    prevContentLengthRef.current = contentLength;

    if (loadMoreRequestedRef.current && addedContent) {
      loadMoreRequestedRef.current = false;
      atTopTriggeredRef.current = false;
      var delta = el.scrollHeight - prevScrollHeightRef.current;

      if (delta > 0) {
        el.scrollTop += delta;
      }

      prevScrollHeightRef.current = el.scrollHeight;
      return;
    }

    prevScrollHeightRef.current = el.scrollHeight;

    if (isStreaming || addedContent && !userScrolledRef.current) {
      var scroll = function scroll() {
        if (el) el.scrollTop = el.scrollHeight;
      };

      requestAnimationFrame(function () {
        return requestAnimationFrame(scroll);
      }); // Also reset userScrolled so streaming auto-scroll continues

      if (isStreaming) {
        userScrolledRef.current = false;
      }
    }
  }, [contentLength, isStreaming]); // Keep scrolling to bottom while streaming content grows (e.g. AI placeholder expands)

  useEffect(function () {
    if (!isStreaming || userScrolledRef.current) return;
    var el = scrollRef.current;
    if (!el) return; // Double rAF to ensure DOM has been painted before scrolling

    var id = requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    });
    return function () {
      return cancelAnimationFrame(id);
    };
  }, [streamingContentSignal, isStreaming]);
  return {
    scrollRef: scrollCallbackRef,
    showScrollToBottom: showScrollToBottom,
    scrollToBottom: scrollToBottom,
    handleScroll: handleScroll
  };
};